import styles from './MoneyDisplay.module.css';

export type Currency = 'ILS' | 'USD' | 'EUR';
export type MoneyDisplayVariant = 'default' | 'large' | 'small' | 'mono';
export type Emphasis = 'positive' | 'negative' | 'neutral';
export type TrendDirection = 'up' | 'down' | 'flat';

export interface MoneyDisplayProps {
  /** הסכום להצגה. `null`/`undefined` → em-dash בצבע subtle */
  amount: number | null | undefined;
  /** מטבע (ברירת מחדל: ש"ח). USD/EUR מציגים $/€ */
  currency?: Currency;
  /** האם להציג סימן מטבע (₪/$/€) */
  showSymbol?: boolean;
  /** מספר ספרות אחרי הנקודה (0 → רק שלמים, 2 → קלאסי) */
  precision?: 0 | 2;
  /** וריאנט גודל: default(14px) | large(32px+bold) | small(12.5px) | mono(JetBrains) */
  variant?: MoneyDisplayVariant;
  /** דגש צבע — לא נגזר אוטומטית מ-amount; המשתמש קובע */
  emphasis?: Emphasis;
  /** קידומת חופשית להציג לפני המספר עבור amount>0 (למשל '+') */
  prefix?: string;
  /** חץ מגמה: up(↑) | down(↓) | flat(→) */
  trend?: TrendDirection;
  /** אחוז שינוי לצד החץ — חיובי יציג עם '+', שלילי עם '-' */
  trendValue?: number;
  /** class CSS נוסף ל-root */
  className?: string;
}

const TREND_ARROWS: Record<TrendDirection, string> = {
  up: '↑',
  down: '↓',
  flat: '→',
};

const TREND_ARIA: Record<TrendDirection, string> = {
  up: 'מגמה עולה',
  down: 'מגמה יורדת',
  flat: 'מגמה יציבה',
};

const cx = (...classes: Array<string | false | null | undefined>): string =>
  classes.filter(Boolean).join(' ');

/**
 * הצגת סכום כספי בפורמט אחיד (Intl.NumberFormat `he-IL`) עם דגש, וריאנט, ומגמה.
 *
 * **שימוש משותף ל-100+ מקומות ב-Profitability** — bedrock primitive.
 * משתמש ב-tokens (`--profit-*`); לא מסתמך על Bootstrap.
 *
 * @example
 * <MoneyDisplay amount={1234.56} />
 * // 1,234.56 ₪
 *
 * @example
 * <MoneyDisplay amount={-500.25} emphasis="negative" />
 *
 * @example
 * <MoneyDisplay
 *   amount={9999}
 *   variant="large"
 *   emphasis="positive"
 *   trend="up"
 *   trendValue={12.5}
 * />
 *
 * @example
 * <MoneyDisplay amount={null} />
 * // —
 */
export const MoneyDisplay = ({
  amount,
  currency = 'ILS',
  showSymbol = true,
  precision = 2,
  variant = 'default',
  emphasis = 'neutral',
  prefix,
  trend,
  trendValue,
  className,
}: MoneyDisplayProps): JSX.Element => {
  // Edge case: null/undefined → em-dash subtle
  if (amount === null || amount === undefined) {
    return (
      <span
        className={cx(styles.root, styles[variant], styles.subtle, className)}
        data-testid="money-display"
      >
        —
      </span>
    );
  }

  // Format עם he-IL locale + currency/decimal style
  const absAmount = Math.abs(amount);
  const formatOptions: Intl.NumberFormatOptions = showSymbol
    ? {
        style: 'currency',
        currency,
        minimumFractionDigits: precision,
        maximumFractionDigits: precision,
      }
    : {
        style: 'decimal',
        minimumFractionDigits: precision,
        maximumFractionDigits: precision,
      };
  const formatted = new Intl.NumberFormat('he-IL', formatOptions).format(absAmount);

  // טיפול בסימן — amount=0 אף פעם בלי סימן, amount<0 → '−', amount>0 → prefix אם סופק
  let sign = '';
  if (amount < 0) {
    sign = '−'; // U+2212 MINUS SIGN — יציב יותר מ-hyphen ל-tabular nums
  } else if (amount > 0 && prefix) {
    sign = prefix;
  }

  const showTrend = trend !== undefined && trend !== null;
  const showTrendValue = trendValue !== undefined && trendValue !== null;

  return (
    <span
      className={cx(styles.root, styles[variant], styles[emphasis], className)}
      data-testid="money-display"
    >
      <span className={styles.value}>
        {sign}
        {formatted}
      </span>
      {showTrend && trend && (
        <span
          className={cx(
            styles.trend,
            trend === 'up' && styles.trendUp,
            trend === 'down' && styles.trendDown,
            trend === 'flat' && styles.trendFlat
          )}
          aria-label={TREND_ARIA[trend]}
          data-testid="money-display-trend"
        >
          <span aria-hidden="true">{TREND_ARROWS[trend]}</span>
          {showTrendValue && (
            <span className={styles.trendValue}>
              {trendValue > 0 ? '+' : ''}
              {trendValue}%
            </span>
          )}
        </span>
      )}
    </span>
  );
};
